import React, { useContext, useEffect, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import bedrockService from '../services/bedrockService';
import { AuthContext } from '../contexts/AuthContext';

export const POLL_INTERVAL_MS = 2000;
export const POLL_TIMEOUT_MS = 180000;

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * ClientDocumentAsk
 *
 * Question box for the client report. Questions are answered by the
 * documents structured knowledge base (text-to-SQL over every extracted data
 * point), scoped to the selected client. Shows the answer, and warns when
 * the SQL was not limited to this client. Admins can also open the SQL that
 * ran and its rows (a developer's check; planners only need the answer).
 */
// clientId scopes the question (the eMoney data is keyed by it); clientName is shown.
const ClientDocumentAsk = ({ clientId, clientName }) => {
  const auth = useContext(AuthContext);
  const showQueryDetails = Boolean(auth && auth.hasPermission && auth.hasPermission('admin'));
  const [question, setQuestion] = useState('');
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  // Questions this client's data can answer, offered after a failed question
  // or one that found nothing.
  const [suggestions, setSuggestions] = useState([]);
  // Bumped on every new question, client change and unmount: a poll loop
  // that sees a newer run id stops without touching state.
  const runIdRef = useRef(0);

  useEffect(() => {
    runIdRef.current += 1;
    setResult(null);
    setError('');
    setSuggestions([]);
    setRunning(false);
  }, [clientId]);

  useEffect(() => () => { runIdRef.current += 1; }, []);

  const ask = async (rawText) => {
    const text = (rawText || '').trim();
    if (!text || !clientId || running) return;

    runIdRef.current += 1;
    const runId = runIdRef.current;
    const isCurrent = () => runIdRef.current === runId;
    setRunning(true);
    setError('');
    setResult(null);
    setSuggestions([]);

    try {
      const { job_id: jobId } = await bedrockService.askDocumentData(clientId, text);
      const deadline = Date.now() + POLL_TIMEOUT_MS;
      while (isCurrent()) {
        await sleep(POLL_INTERVAL_MS);
        if (!isCurrent()) return;
        const job = await bedrockService.getDocumentDataAnswer(jobId);
        if (!isCurrent()) return;
        if (job.status === 'completed') {
          setResult(job);
          setSuggestions(job.suggestions || []);
          break;
        }
        if (job.status === 'failed') {
          setError(job.error || 'The question could not be answered.');
          setSuggestions(job.suggestions || []);
          break;
        }
        if (Date.now() > deadline) {
          setError('Timed out waiting for an answer. Try a narrower question.');
          break;
        }
      }
    } catch (err) {
      console.error('Error asking about document data:', err);
      if (isCurrent()) setError('Could not ask the question. Please try again.');
    } finally {
      if (isCurrent()) setRunning(false);
    }
  };

  const handleAsk = (e) => {
    e.preventDefault();
    ask(question);
  };

  const askSuggestion = (text) => {
    setQuestion(text);
    ask(text);
  };

  const rows = (result && result.rows) || [];
  const columns = rows.length ? Object.keys(rows[0]) : [];

  return (
    <div className="card mb-3" data-testid="client-document-ask">
      <div className="card-body py-2">
        <form className="d-flex gap-2" onSubmit={handleAsk}>
          <input
            type="text"
            className="form-control form-control-sm"
            placeholder={`Ask about ${clientName}'s documents, e.g. "What is the ending balance on each bank account?"`}
            value={question}
            maxLength={1000}
            onChange={(e) => setQuestion(e.target.value)}
            disabled={running}
            aria-label="Question about this client's documents"
          />
          <button type="submit" className="btn btn-sm btn-primary text-nowrap" disabled={running || !question.trim()}>
            {running ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Asking...
              </>
            ) : (
              <>
                <i className="bi bi-chat-dots me-1"></i>Ask
              </>
            )}
          </button>
        </form>

        {error && (
          <div className="alert alert-warning py-2 mt-2 mb-0 small" role="alert" data-testid="ask-error">
            {error}
          </div>
        )}

        {result && (
          <div className="mt-2" data-testid="ask-result">
            {result.scope_verified === false && (
              <div className="alert alert-warning py-2 small" data-testid="ask-scope-warning">
                <i className="bi bi-exclamation-triangle me-2"></i>
                This answer may include data from other clients: the query that ran was not limited to {clientName}.
                {showQueryDetails && ' Check the SQL below.'}
              </div>
            )}
            <div className="small"><ReactMarkdown>{result.answer || 'No answer was returned.'}</ReactMarkdown></div>
            {showQueryDetails && (result.generated_sql || []).length > 0 && (
              <details className="small">
                <summary className="text-muted">Show query and results ({rows.length} rows)</summary>
                {result.generated_sql.map((sql) => (
                  <pre key={sql} className="bg-light border rounded p-2 mt-2 mb-2" style={{ whiteSpace: 'pre-wrap' }}>{sql}</pre>
                ))}
                {rows.length > 0 && (
                  <div className="table-responsive">
                    <table className="table table-sm table-bordered mb-0">
                      <thead>
                        <tr>{columns.map((col) => <th key={col} className="small bg-light">{col}</th>)}</tr>
                      </thead>
                      <tbody>
                        {rows.map((row, idx) => (
                          <tr key={idx}>{columns.map((col) => <td key={col} className="small">{row[col] ?? ''}</td>)}</tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </details>
            )}
          </div>
        )}

        {!running && suggestions.length > 0 && (
          <div className="mt-2 small" data-testid="ask-suggestions">
            <span className="text-muted me-2">Try asking:</span>
            {suggestions.map((text) => (
              <button
                key={text}
                type="button"
                className="btn btn-outline-secondary btn-sm me-2 mb-1"
                onClick={() => askSuggestion(text)}
              >
                {text}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ClientDocumentAsk;
